import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Feature-detect the Web Speech API (spec "Voice input (STT)"): Firefox has
 * neither `SpeechRecognition` nor the vendor-prefixed `webkitSpeechRecognition`
 * — callers must hide the mic button entirely rather than show it
 * disabled-and-mysterious.
 */
export function isSpeechRecognitionSupported() {
  return typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * Toggled (not push-to-talk) dictation: `continuous: true, interimResults:
 * true`; finalised segments are handed to `onTranscript(text)` so the caller
 * can insert them at the field's caret — this hook never touches the DOM
 * itself, keeping it reusable for both CommentComposer's textarea and
 * ReviewFooter's input (spec: "make it a small reusable piece").
 *
 * No audio or transcript ever reaches RMDV servers — recognition is entirely
 * the browser's (Security §8). The mic is force-released (`stop()`) by the
 * caller on Save/Cancel/Esc/unmount — this hook itself also stops recognition
 * on unmount as a last-resort safety net against a hot mic after navigation.
 *
 * @param {{ onTranscript?: (text: string) => void }} [opts]
 */
export function useSpeechToText({ onTranscript } = {}) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  // Always-current ref so a stale closure never drops a transcript callback
  // change without needing to restart recognition.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const supported = isSpeechRecognitionSupported();

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Already stopped/never started — nothing to do.
      }
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result && result.isFinal && onTranscriptRef.current) {
          onTranscriptRef.current(result[0].transcript);
        }
      }
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [supported]);

  const toggle = useCallback(() => {
    if (listening) stop(); else start();
  }, [listening, start, stop]);

  // Force-release the mic on unmount — no hot-mic-after-navigation (Security §8).
  useEffect(() => () => stop(), [stop]);

  return { supported, listening, start, stop, toggle };
}

export default useSpeechToText;
